// Arena controls (lane C, backlog C-10, C-16, C-21; ui spec §6):
// - mouse: heading from the screen centre (the own head) to the cursor, applied instantly, no
//   dead zone; leaving the window or blur keeps the last heading and sends nothing;
// - keyboard: arrows / WASD sum to an 8-way target (Up+Right = 45°), sent as a heading; the
//   mouse takes over again on any move; releasing every key keeps the heading;
// - touch: a floating heading-only stick (`HeadingStick`: dead zone stickDeadZonePx, the anchor
//   follows beyond stickFollowPx, lifting keeps the heading; ring 120 px, knob 48 px, fading
//   200 ms after the lift), or "point" mode (setting `arenaSteer`: finger relative to the screen
//   centre). No Split, Shoot or pinch. The cash-out hold button lives in the HUD (hud.ts).
// - Q (the app's KeyboardInput `cashOutHeld`) stays the cash-out key; nothing here.
// Output (`frame`): ARENA_INPUT through `sink.sendHeading(steps, seq)` when the quantised heading
// changes (at display rate, but never sooner than MIN_SEND_MS after the last send: ≤ 60/s, the
// protocol §6.6 upstream budget on 120/144 Hz displays; the newest heading goes out on the next
// allowed frame), else every HEARTBEAT_MS while steering is live (pointer in the window, a key
// or a finger down); nothing while the heading is held. The sink also feeds the predictor, so
// what is sent is exactly what is predicted (both quantised by headingToWire).
// - The mouse position is remembered while disabled (menu open): when steering turns on with
//   the cursor in the window and no heading yet, the head turns toward the resting cursor at once
//   (paperio applies the cursor in the first frame), without waiting for a mouse move.

import { ARENA_CLIENT, headingToWire } from '@ballio/shared';

/** Where controls send: the socket and the predictor. */
export interface ArenaInputSink {
  sendHeading(headingSteps: number, seq: number): void;
  cashOut(down: boolean): void;
}

/** An unchanged heading is re-sent this often while steering is live (protocol §5.1). */
export const HEARTBEAT_MS = 250;
/**
 * A changed heading waits at least this long after the last send: one per frame at 60 Hz,
 * every 2nd at 120 Hz and every 3rd at 144 Hz (≤ 60 × 5 B = 0.3 KB/s, protocol §6.6). The sim
 * applies one heading per 40 ms tick, so this is still sub-tick fresh.
 */
export const MIN_SEND_MS = 1000 / 60 - 0.5;
/** The stick ring fades this long after the lift. */
export const STICK_FADE_MS = 200;
const STICK_RING_PX = 120;
const STICK_KNOB_PX = 48;

const DIAG = Math.SQRT1_2;

/** The 8-way keyboard target in radians (0 = +x, +y down), or null with no key down. */
export function keyboardHeading(
  up: boolean,
  down: boolean,
  left: boolean,
  right: boolean,
): number | null {
  const x = (right ? 1 : 0) - (left ? 1 : 0);
  const y = (down ? 1 : 0) - (up ? 1 : 0);
  if (x === 0 && y === 0) return null;
  const a = Math.atan2(x !== 0 && y !== 0 ? y * DIAG : y, x !== 0 && y !== 0 ? x * DIAG : x);
  return a < 0 ? a + Math.PI * 2 : a;
}

/** Heading from the screen centre to (px, py), or null at the centre itself. */
export function pointHeading(px: number, py: number, width: number, height: number): number | null {
  const dx = px - width / 2;
  const dy = py - height / 2;
  if (dx === 0 && dy === 0) return null;
  const a = Math.atan2(dy, dx);
  return a < 0 ? a + Math.PI * 2 : a;
}

/**
 * The floating heading-only stick (ui spec §6). Heading = finger − anchor; inside the dead zone
 * the heading is unchanged; beyond `followPx` the anchor is dragged along the line so a reversal
 * needs only a short flick.
 */
export class HeadingStick {
  anchorX = 0;
  anchorY = 0;
  fingerX = 0;
  fingerY = 0;
  active = false;
  readonly deadZonePx: number;
  readonly followPx: number;

  constructor(
    deadZonePx: number = ARENA_CLIENT.stickDeadZonePx,
    followPx: number = ARENA_CLIENT.stickFollowPx,
  ) {
    this.deadZonePx = deadZonePx;
    this.followPx = followPx;
  }

  down(x: number, y: number): void {
    this.active = true;
    this.anchorX = this.fingerX = x;
    this.anchorY = this.fingerY = y;
  }

  /** The new heading (radians), or null inside the dead zone (keep the old one). */
  move(x: number, y: number): number | null {
    if (!this.active) return null;
    this.fingerX = x;
    this.fingerY = y;
    let dx = x - this.anchorX;
    let dy = y - this.anchorY;
    const d = Math.hypot(dx, dy);
    if (d < this.deadZonePx) return null;
    if (d > this.followPx) {
      this.anchorX = x - (dx / d) * this.followPx;
      this.anchorY = y - (dy / d) * this.followPx;
      dx = x - this.anchorX;
      dy = y - this.anchorY;
    }
    const a = Math.atan2(dy, dx);
    return a < 0 ? a + Math.PI * 2 : a;
  }

  up(): void {
    this.active = false;
  }
}

/**
 * The send rule (pure): what to send this frame, given the target heading, the last sent steps
 * and time, and whether steering is live. Returns the steps to send, or null.
 */
export function headingToSend(
  heading: number | null,
  lastSteps: number | null,
  lastSentMs: number,
  nowMs: number,
  live: boolean,
): number | null {
  if (heading === null) return null;
  const steps = headingToWire(heading);
  if (steps !== lastSteps) return nowMs - lastSentMs >= MIN_SEND_MS ? steps : null;
  if (live && nowMs - lastSentMs >= HEARTBEAT_MS) return steps;
  return null;
}

const KEY_DIRS: Readonly<Record<string, 'up' | 'down' | 'left' | 'right'>> = Object.freeze({
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
});

export type ArenaSteerMode = 'stick' | 'point';

export interface ArenaControlsOptions {
  readonly window: Window;
  /** The game canvas (pointer and touch input). */
  readonly canvas: HTMLElement;
  /** Where the stick overlay is mounted (the HUD layer). */
  readonly overlayRoot: HTMLElement;
  readonly sink: ArenaInputSink;
  /** Touch steering style (setting `arenaSteer`). */
  readonly steerMode: () => ArenaSteerMode;
}

export class ArenaControls {
  private readonly o: ArenaControlsOptions;
  private attached = false;
  private enabled = false;
  private heading: number | null = null;
  private lastSteps: number | null = null;
  private lastSentMs = Number.NEGATIVE_INFINITY;
  private seq = 0;
  private pointerInside = false;
  /** The last mouse position (CSS px), kept while disabled; null before any mouse move. */
  private mouse: { x: number; y: number } | null = null;
  private readonly keys = { up: false, down: false, left: false, right: false };
  private readonly stick = new HeadingStick();
  private touchId: number | null = null;
  private stickEl: HTMLElement | null = null;
  private knobEl: HTMLElement | null = null;
  private stickFadeAtMs = Number.NEGATIVE_INFINITY;
  private readonly offs: (() => void)[] = [];

  constructor(o: ArenaControlsOptions) {
    this.o = o;
  }

  /** The heading the controls steer toward (radians), or null before any input. */
  get target(): number | null {
    return this.heading;
  }

  /** Starts listening (pointer, keys, touch). */
  attach(): void {
    if (this.attached) return;
    this.attached = true;
    const { window: win, canvas } = this.o;
    const on = <K extends keyof WindowEventMap>(
      t: EventTarget,
      type: K | string,
      fn: (ev: Event) => void,
      opts: AddEventListenerOptions = { passive: true },
    ): void => {
      t.addEventListener(type, fn, opts);
      this.offs.push(() => t.removeEventListener(type, fn, opts));
    };
    on(win, 'pointermove', (ev) => this.onPointerMove(ev as PointerEvent));
    on(canvas, 'pointerdown', (ev) => this.onPointerDown(ev as PointerEvent));
    on(win, 'pointerup', (ev) => this.onPointerUp(ev as PointerEvent));
    on(win, 'pointercancel', (ev) => this.onPointerUp(ev as PointerEvent));
    on(win.document, 'pointerleave', () => (this.pointerInside = false));
    on(win.document.documentElement, 'mouseleave', () => (this.pointerInside = false));
    on(win, 'blur', () => this.onBlur());
    on(win, 'keydown', (ev) => this.onKey(ev as KeyboardEvent, true));
    on(win, 'keyup', (ev) => this.onKey(ev as KeyboardEvent, false));
  }

  detach(): void {
    for (const off of this.offs.splice(0)) off();
    this.attached = false;
    this.stickEl?.remove();
    this.stickEl = null;
    this.knobEl = null;
  }

  /** Steering only while a life is playing with the menu closed. */
  setEnabled(on: boolean): void {
    if (this.enabled === on) return;
    this.enabled = on;
    if (!on) {
      this.stick.up();
      this.touchId = null;
      this.keys.up = this.keys.down = this.keys.left = this.keys.right = false;
      return;
    }
    this.aimAtRestingCursor();
  }

  /**
   * A new life: forget the old heading (the first input is the first sample), then, if steering
   * is on, aim at the resting cursor.
   */
  resetLife(): void {
    this.heading = null;
    this.lastSteps = null;
    this.lastSentMs = Number.NEGATIVE_INFINITY;
    if (this.enabled) this.aimAtRestingCursor();
  }

  /** No heading yet and the mouse rests in the window: steer toward it (sent by `frame`). */
  private aimAtRestingCursor(): void {
    if (this.heading !== null || !this.pointerInside || this.mouse === null) return;
    const { w, h } = this.size();
    const a = pointHeading(this.mouse.x, this.mouse.y, w, h);
    if (a !== null) this.heading = a;
  }

  /** Once per display frame: sends the heading per `headingToSend`, draws the stick. */
  frame(nowMs: number): void {
    this.drawStick(nowMs);
    if (!this.enabled) return;
    const live =
      this.pointerInside ||
      this.stick.active ||
      this.keys.up ||
      this.keys.down ||
      this.keys.left ||
      this.keys.right;
    const steps = headingToSend(this.heading, this.lastSteps, this.lastSentMs, nowMs, live);
    if (steps === null) return;
    this.seq = (this.seq + 1) & 0xffff;
    this.lastSteps = steps;
    this.lastSentMs = nowMs;
    this.o.sink.sendHeading(steps, this.seq);
  }

  private size(): { w: number; h: number } {
    const win = this.o.window;
    return { w: Math.max(1, win.innerWidth), h: Math.max(1, win.innerHeight) };
  }

  private onPointerMove(ev: PointerEvent): void {
    if (ev.pointerType === 'touch') {
      if (ev.pointerId !== this.touchId || !this.enabled) return;
      if (this.o.steerMode() === 'point') {
        const { w, h } = this.size();
        const a = pointHeading(ev.clientX, ev.clientY, w, h);
        if (a !== null) this.heading = a;
      } else {
        const a = this.stick.move(ev.clientX, ev.clientY);
        if (a !== null) this.heading = a;
      }
      return;
    }
    this.pointerInside = true;
    this.mouse = { x: ev.clientX, y: ev.clientY };
    if (!this.enabled) return;
    const { w, h } = this.size();
    const a = pointHeading(ev.clientX, ev.clientY, w, h);
    if (a !== null) this.heading = a;
  }

  private onPointerDown(ev: PointerEvent): void {
    if (ev.pointerType !== 'touch') {
      this.pointerInside = true;
      return;
    }
    if (!this.enabled || this.touchId !== null) return;
    this.touchId = ev.pointerId;
    if (this.o.steerMode() === 'point') {
      const { w, h } = this.size();
      const a = pointHeading(ev.clientX, ev.clientY, w, h);
      if (a !== null) this.heading = a;
      this.stick.active = true;
    } else {
      this.stick.down(ev.clientX, ev.clientY);
    }
  }

  private onPointerUp(ev: PointerEvent): void {
    if (ev.pointerId !== this.touchId) return;
    this.touchId = null;
    this.stick.up();
    this.stickFadeAtMs = performance.now();
  }

  private onBlur(): void {
    this.pointerInside = false;
    this.keys.up = this.keys.down = this.keys.left = this.keys.right = false;
  }

  private onKey(ev: KeyboardEvent, down: boolean): void {
    const dir = KEY_DIRS[ev.code];
    if (!dir) return;
    const t = ev.target as { closest?: (sel: string) => unknown } | null;
    if (t?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
    this.keys[dir] = down;
    if (!this.enabled) return;
    const a = keyboardHeading(this.keys.up, this.keys.down, this.keys.left, this.keys.right);
    if (a !== null) this.heading = a; // releasing every key keeps the heading
  }

  private drawStick(nowMs: number): void {
    const show = this.enabled && this.o.steerMode() === 'stick';
    const fading = !this.stick.active && nowMs - this.stickFadeAtMs < STICK_FADE_MS;
    if (!show || (!this.stick.active && !fading)) {
      if (this.stickEl) this.stickEl.style.opacity = '0';
      return;
    }
    if (!this.stickEl) this.buildStick();
    const el = this.stickEl as HTMLElement;
    const knob = this.knobEl as HTMLElement;
    el.style.opacity = this.stick.active
      ? '1'
      : String(Math.max(0, 1 - (nowMs - this.stickFadeAtMs) / STICK_FADE_MS));
    el.style.transform = `translate(${this.stick.anchorX - STICK_RING_PX / 2}px, ${
      this.stick.anchorY - STICK_RING_PX / 2
    }px)`;
    let kx = this.stick.fingerX - this.stick.anchorX;
    let ky = this.stick.fingerY - this.stick.anchorY;
    const d = Math.hypot(kx, ky);
    const max = (STICK_RING_PX - STICK_KNOB_PX) / 2;
    if (d > max) {
      kx = (kx / d) * max;
      ky = (ky / d) * max;
    }
    knob.style.transform = `translate(${kx}px, ${ky}px)`;
  }

  private buildStick(): void {
    const doc = this.o.window.document;
    const ring = doc.createElement('div');
    ring.className = 'arena-stick';
    const knob = doc.createElement('div');
    knob.className = 'arena-stick-knob';
    ring.append(knob);
    this.o.overlayRoot.append(ring);
    this.stickEl = ring;
    this.knobEl = knob;
  }
}
